import {
  addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp,
  runTransaction, setDoc, Timestamp, updateDoc, where, writeBatch, type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from './firebase';
import { approvalOutcome } from './calculations';
import type { AppUser, Attachment, Deadline, Evaluation, OwnerTask, RequestMessage, Task, TaskProgress, TaskTemplate, UserDirectoryEntry, WorkRequest } from '../types';

export interface WithId<T> { id: string; data: T }

async function withTimeout<T>(operation: Promise<T>, milliseconds = 10_000): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error('save-timeout'), { code: 'app/timeout' })), milliseconds); });
  try { return await Promise.race([operation, timeout]); } finally { if (timer) clearTimeout(timer); }
}

export function nextOccurrence(dueAt: Timestamp, frequency: NonNullable<Task['recurrence']>['frequency']): Timestamp {
  const next = dueAt.toDate();
  if (frequency === 'daily') next.setUTCDate(next.getUTCDate() + 1);
  if (frequency === 'weekly') next.setUTCDate(next.getUTCDate() + 7);
  if (frequency === 'monthly') {
    const day = next.getUTCDate();
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 1);
    const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
    next.setUTCDate(Math.min(day, lastDay));
  }
  return Timestamp.fromDate(next);
}

function validateAttachment(file: File): void {
  if (file.size > 10 * 1024 * 1024) throw Object.assign(new Error('attachment-too-large'), { code: 'attachment/too-large' });
  if (!file.type.startsWith('image/') && file.type !== 'application/pdf') throw Object.assign(new Error('attachment-type'), { code: 'attachment/type' });
}

async function uploadRequestAttachment(requesterId: string, requestId: string, file: File): Promise<Attachment> {
  validateAttachment(file);
  const safeName = `${Date.now()}-${file.name.replace(/[^\p{L}\p{N}._-]/gu, '_')}`;
  const uploadRef = ref(storage, `request-attachments/${requesterId}/${requestId}/${safeName}`);
  const snapshot = await withTimeout(uploadBytes(uploadRef, file, { contentType: file.type }), 30_000);
  return { name: file.name, url: await getDownloadURL(snapshot.ref), contentType: file.type, size: file.size };
}

export function listenUsers(callback: (items: WithId<AppUser>[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(collection(db, 'users'), (snap) => callback(snap.docs.map((item) => ({ id: item.id, data: item.data() as AppUser }))), onError);
}

export function listenRequestDirectory(role: AppUser['role'], callback: (items: WithId<UserDirectoryEntry>[]) => void, onError: (error: Error) => void): Unsubscribe {
  const source = role === 'employee' ? collection(db, 'directoryProfiles') : collection(db, 'users');
  return onSnapshot(source, (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, data: item.data() as UserDirectoryEntry }))), onError);
}

function mergeListeners<T>(sources: ReturnType<typeof query>[], callback: (items: WithId<T>[]) => void, onError: (error: Error) => void): Unsubscribe {
  if (!sources.length) { callback([]); return () => undefined; }
  const snapshots = new Map<number, WithId<T>[]>();
  const unsubs = sources.map((source, index) => onSnapshot(source, (snap) => {
    snapshots.set(index, snap.docs.map((item) => ({ id: item.id, data: item.data() as T })));
    const merged = new Map<string, WithId<T>>(); snapshots.forEach((items) => items.forEach((item) => merged.set(item.id, item)));
    callback([...merged.values()]);
  }, onError));
  return () => unsubs.forEach((unsubscribe) => unsubscribe());
}

export function listenTasks(uid: string, profile: AppUser, callback: (items: WithId<Task>[]) => void, onError: (error: Error) => void): Unsubscribe {
  const base = collection(db, 'tasks');
  if (profile.role === 'employee') return mergeListeners<Task>([query(base, where('assigneeIds', 'array-contains', uid))], callback, onError);
  return mergeListeners<Task>([query(base, orderBy('currentDueAt', 'asc'))], callback, onError);
}

export function listenProgress(taskId: string, callback: (items: TaskProgress[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(collection(db, 'tasks', taskId, 'progress'), (snap) => callback(snap.docs.map((item) => item.data() as TaskProgress)), onError);
}

export async function createTask(input: Omit<Task, 'createdAt' | 'updatedAt' | 'approval'>): Promise<string> {
  const batch = writeBatch(db);
  const taskRef = doc(collection(db, 'tasks'));
  let recurrence = input.recurrence;
  if (input.recurrence) {
    const templateRef = doc(collection(db, 'taskTemplates'));
    recurrence = { ...input.recurrence, templateId: templateRef.id };
    const template: Omit<TaskTemplate, 'createdAt' | 'updatedAt'> = {
      title: input.title, description: input.description, assigneeIds: input.assigneeIds,
      creatorId: input.creatorId, organizationId: input.organizationId, departmentId: input.departmentId,
      priority: input.priority, frequency: input.recurrence.frequency, timezone: input.recurrence.timezone,
      nextRunAt: nextOccurrence(input.currentDueAt, input.recurrence.frequency), active: true,
    };
    batch.set(templateRef, { ...template, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
  batch.set(taskRef, { ...input, recurrence, approval: { status: 'none', approverId: null, decidedAt: null, reason: null }, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  for (const uid of input.assigneeIds) {
    batch.set(doc(taskRef, 'progress', uid), { userId: uid, percent: 0, status: 'open', completedAt: null, updatedAt: serverTimestamp() });
  }
  await withTimeout(batch.commit());
  return taskRef.id;
}

export async function completeAssignment(taskId: string): Promise<{ pendingApproval: boolean }> {
  const result = await withTimeout(httpsCallable<{ taskId: string }, { pendingApproval: boolean }>(functions, 'completeTaskAssignment')({ taskId }), 20_000);
  return result.data;
}

export async function decideTask(taskId: string, approverId: string, approved: boolean, reason: string | null): Promise<void> {
  const outcome = approvalOutcome(approved);
  await withTimeout(updateDoc(doc(db, 'tasks', taskId), {
    status: outcome.status,
    ...(outcome.shouldArchive ? { archivedAt: serverTimestamp() } : {}),
    approval: { status: outcome.approvalStatus, approverId, decidedAt: serverTimestamp(), reason },
    updatedAt: serverTimestamp(),
  }));
}

export async function archiveTask(taskId: string): Promise<void> {
  await withTimeout(updateDoc(doc(db, 'tasks', taskId), {
    status: 'archived', archivedAt: serverTimestamp(), updatedAt: serverTimestamp(),
  }));
}

export async function archiveCompletedTasks(tasks: WithId<Task>[]): Promise<number> {
  const completedIds = tasks.filter((task) => task.data.status === 'completed').map((task) => task.id);
  for (let index = 0; index < completedIds.length; index += 400) {
    const batch = writeBatch(db);
    for (const taskId of completedIds.slice(index, index + 400)) {
      batch.update(doc(db, 'tasks', taskId), {
        status: 'archived', archivedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
    }
    await withTimeout(batch.commit());
  }
  return completedIds.length;
}

export function listenRequests(uid: string, role: AppUser['role'], callback: (items: WithId<WorkRequest>[]) => void, onError: (error: Error) => void): Unsubscribe {
  const base = collection(db, 'requests');
  const sources = role === 'owner' || role === 'supervisor'
    ? [query(base, orderBy('createdAt', 'desc'))]
    : [query(base, where('recipientId', '==', uid)), query(base, where('requesterId', '==', uid))];
  return mergeListeners<WorkRequest>(sources, callback, onError);
}

export function listenTeam(profile: AppUser, callback: (items: WithId<AppUser>[]) => void, onError: (error: Error) => void): Unsubscribe {
  const base = collection(db, 'users');
  if (profile.role === 'employee') return mergeListeners<AppUser>([], callback, onError);
  return mergeListeners<AppUser>([query(base)], callback, onError);
}

export function listenEvaluations(uid: string, role: AppUser['role'], callback: (items: WithId<Evaluation>[]) => void, onError: (error: Error) => void): Unsubscribe {
  const base = collection(db, 'evaluations');
  const sources = role === 'owner' ? [query(base)] : [query(base, where(role === 'employee' ? 'employeeId' : 'evaluatorId', '==', uid))];
  return mergeListeners<Evaluation>(sources, callback, onError);
}

export async function createRequest(input: Omit<WorkRequest, 'status' | 'firstDecisionAt' | 'rejectionReason' | 'createdAt' | 'updatedAt' | 'attachments'>, attachment?: File | null): Promise<void> {
  const requestRef = doc(collection(db, 'requests'));
  const attachments = attachment ? [await uploadRequestAttachment(input.requesterId, requestRef.id, attachment)] : [];
  await withTimeout(setDoc(requestRef, { ...input, attachments, status: 'pending', firstDecisionAt: null, rejectionReason: null, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
}

export async function decideRequest(id: string, status: 'accepted' | 'rejected' | 'clarification_requested', rejectionReason: string | null): Promise<void> {
  if (status !== 'accepted' && !rejectionReason?.trim()) throw new Error('rejection-reason-required');
  await withTimeout(runTransaction(db, async (transaction) => {
    const requestRef = doc(db, 'requests', id);
    const snapshot = await transaction.get(requestRef);
    if (!snapshot.exists()) throw Object.assign(new Error('request-not-found'), { code: 'request/not-found' });
    const request = snapshot.data() as WorkRequest;
    if (request.status !== 'pending') throw Object.assign(new Error('request-already-decided'), { code: 'request/already-decided' });
    if (status === 'accepted' && request.kind === 'task_extension') {
      if (!request.taskId || !request.requestedDueAt) throw Object.assign(new Error('invalid-extension-request'), { code: 'request/invalid-extension' });
      transaction.update(doc(db, 'tasks', request.taskId), { currentDueAt: request.requestedDueAt, updatedAt: serverTimestamp() });
    }
    transaction.update(requestRef, { status, rejectionReason, firstDecisionAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }));
}

export function listenRequestMessages(requestId: string, callback: (items: WithId<RequestMessage>[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(query(collection(db, 'requests', requestId, 'messages'), orderBy('createdAt', 'asc')), (snapshot) => {
    callback(snapshot.docs.map((item) => ({ id: item.id, data: item.data() as RequestMessage })));
  }, onError);
}

export async function addRequestMessage(requestId: string, authorId: string, text: string, attachment?: File | null): Promise<void> {
  const cleanText = text.trim();
  if (!cleanText && !attachment) throw new Error('message-empty');
  const messageRef = doc(collection(db, 'requests', requestId, 'messages'));
  const attachments = attachment ? [await uploadRequestAttachment(authorId, requestId, attachment)] : [];
  await withTimeout(runTransaction(db, async (transaction) => {
    const requestRef = doc(db, 'requests', requestId);
    const snapshot = await transaction.get(requestRef);
    if (!snapshot.exists()) throw Object.assign(new Error('request-not-found'), { code: 'request/not-found' });
    const request = snapshot.data() as WorkRequest;
    if (['accepted', 'rejected', 'completed'].includes(request.status)) throw Object.assign(new Error('request-closed'), { code: 'request/closed' });
    transaction.set(messageRef, { authorId, text: cleanText, attachments, createdAt: serverTimestamp() });
    transaction.update(requestRef, {
      ...(request.status === 'clarification_requested' && request.requesterId === authorId ? { status: 'pending', rejectionReason: null } : {}),
      updatedAt: serverTimestamp(),
    });
  }));
}

export function listenOwnerTasks(uid: string, callback: (items: WithId<OwnerTask>[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(query(collection(db, 'ownerTasks'), where('ownerId', '==', uid)), (snapshot) => {
    const items = snapshot.docs.map((item) => ({ id: item.id, data: item.data() as OwnerTask }));
    items.sort((a, b) => a.data.dueAt.toMillis() - b.data.dueAt.toMillis());
    callback(items);
  }, onError);
}

export async function createOwnerTask(input: Omit<OwnerTask, 'status' | 'createdAt' | 'updatedAt'>): Promise<void> {
  await withTimeout(addDoc(collection(db, 'ownerTasks'), { ...input, status: 'open', createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
}

export async function setOwnerTaskCompleted(id: string, completed: boolean): Promise<void> {
  await withTimeout(updateDoc(doc(db, 'ownerTasks', id), { status: completed ? 'completed' : 'open', updatedAt: serverTimestamp() }));
}

export function listenCollection<T>(name: string, callback: (items: WithId<T>[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(collection(db, name), (snap) => callback(snap.docs.map((item) => ({ id: item.id, data: item.data() as T }))), onError);
}

export interface NotificationRecord { recipientId: string; actorId?: string; type?: string; title: string; body: string; link: string; count: number; readAt: Timestamp | null; createdAt: Timestamp; updatedAt?: Timestamp; channelStatus?: Record<string, { status: string; detail: string }> }
export function listenNotifications(uid: string, callback: (items: WithId<NotificationRecord>[]) => void, onError: (error: Error) => void): Unsubscribe {
  return onSnapshot(query(collection(db, 'notifications'), where('recipientId', '==', uid)), (snapshot) => {
    const items = snapshot.docs.map((item) => ({ id: item.id, data: item.data() as NotificationRecord }));
    items.sort((a, b) => (b.data.createdAt?.toMillis?.() ?? 0) - (a.data.createdAt?.toMillis?.() ?? 0));
    callback(items);
  }, onError);
}

export async function saveDeadline(input: Omit<Deadline, 'status'>): Promise<void> {
  await withTimeout(addDoc(collection(db, 'deadlines'), { ...input, status: 'active', createdAt: serverTimestamp() }));
}

export async function saveEvaluation(input: Omit<Evaluation, 'createdAt'>): Promise<void> {
  await withTimeout(addDoc(collection(db, 'evaluations'), { ...input, createdAt: serverTimestamp() }));
}

export async function markNotificationRead(id: string): Promise<void> {
  await updateDoc(doc(db, 'notifications', id), { readAt: serverTimestamp() });
}

export async function getUser(uid: string): Promise<AppUser | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() as AppUser : null;
}

export async function updateReference(id: string, values: string[]): Promise<void> {
  await setDoc(doc(db, 'referenceData', id), { values, updatedAt: serverTimestamp() }, { merge: true });
}

export function toTimestamp(dateValue: string): Timestamp { return Timestamp.fromDate(new Date(`${dateValue}T12:00:00+03:00`)); }
